import { zodResolver } from '@hookform/resolvers/zod'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import { Button, Modal, Switch } from '@surecompanies/core_components'
import RHFInput from 'enrollment-platform/components/form/hookform/Input/RHFInput'
import RHFToggle from 'enrollment-platform/components/form/hookform/Toogle/RHFToogle'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { CensusApiVersion } from '../../../../hooks/useCustomerConfiguration'
import { IntegrationSchema } from './integration.schema'

type FormData = z.infer<typeof IntegrationSchema>

type IntegrationFormProps = {
  initialValues: any
  censusApiVersion: CensusApiVersion
  isCensusApiVersionDisabled: boolean
  isUpdatingCensusApiVersion: boolean
  isUpdatingHrisIntegration: boolean
  onCensusApiVersionChange: (version: CensusApiVersion) => void
  onFormSubmit: (data: FormData) => Promise<unknown>
}

export default function IntegrationForm({
  initialValues,
  censusApiVersion,
  isCensusApiVersionDisabled,
  isUpdatingCensusApiVersion,
  isUpdatingHrisIntegration,
  onCensusApiVersionChange,
  onFormSubmit,
}: IntegrationFormProps) {
  const { control, handleSubmit, setValue, trigger, watch } = useForm<FormData>(
    {
      defaultValues: initialValues ?? {},
      resolver: zodResolver(IntegrationSchema),
    }
  )

  const onSubmit = async (data: FormData) => {
    await onFormSubmit(data)
  }

  const getWebhookUrlValidation = () => {
    const webHookUrl = watch('webHookUrl')?.trim() ?? ''

    if (!webHookUrl) {
      return {
        message: 'Please update the webhook URL before changing API sync.',
        requirement: 'update the webhook URL',
      }
    }

    if (!IntegrationSchema.shape.webHookUrl.safeParse(webHookUrl).success) {
      return {
        message: 'Please enter a valid webhook URL before changing API sync.',
        requirement: 'enter a valid webhook URL',
      }
    }

    return null
  }

  const handleCensusSyncEnabledChange = (isEnabled: boolean) => {
    const actionLabel = isEnabled ? 'turn on' : 'turn off'
    const webhookValidation = getWebhookUrlValidation()

    if (isEnabled && webhookValidation) {
      Modal.error({
        title: 'Webhook URL required',
        content: webhookValidation.message,
      })
      return
    }

    Modal.confirm({
      title: `${isEnabled ? 'Turn on' : 'Turn off'} API sync?`,
      content: (
        <>
          <div>Are you sure you want to {actionLabel} API sync?</div>
          <div>
            {isEnabled
              ? 'Please make sure you have updated the webhook URL to the new version.'
              : 'This will update the HRIS integration immediately.'}
          </div>
          <br />
          <div>
            All configurations in this page will be updated immediately.
          </div>
        </>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        await handleSubmit(async (data) => {
          const nextData = {
            ...data,
            isCensusSyncEnabled: isEnabled,
          }

          await onSubmit(nextData)
          setValue('isCensusSyncEnabled', isEnabled, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })
        })()
      },
    })
  }

  const handleCensusApiVersionChange = (isVersion2: boolean) => {
    const nextVersion = isVersion2 ? 'v2' : 'v1'
    const nextVersionLabel = nextVersion === 'v2' ? 'CMv2' : 'CMv1'
    const missingRequirements: string[] = []
    const webhookValidation = getWebhookUrlValidation()

    if (!watch('isCensusSyncEnabled')) {
      missingRequirements.push('turn on API sync')
    }

    if (webhookValidation) {
      missingRequirements.push(webhookValidation.requirement)
    }

    if (missingRequirements.length > 0) {
      Modal.error({
        title: `Cannot switch to ${nextVersionLabel}`,
        content: `Please ${missingRequirements.join(
          ' and '
        )} before switching the API sync version.`,
      })
      return
    }

    Modal.confirm({
      title: `Switch API sync to ${nextVersionLabel}?`,
      content: (
        <>
          <div>
            Are you sure you want to sync the HRIS API with {nextVersionLabel}?
          </div>
          <div>
            Please make sure API sync is enabled and the webhook URL is updated
            to the new version.
          </div>
        </>
      ),
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        onCensusApiVersionChange(nextVersion)
      },
    })
  }

  const handleSaveClick = async () => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }

    Modal.confirm({
      title: 'Save HRIS integration configuration?',
      content:
        'All configurations in this page will be saved, api sync will be turn off if the webhook url is empty.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        await handleSubmit(async (data) => {
          const shouldDisableCensusSync = !data.webHookUrl.trim()
          const nextData = {
            ...data,
            isCensusSyncEnabled: shouldDisableCensusSync
              ? false
              : data.isCensusSyncEnabled,
          }

          await onSubmit(nextData)

          if (shouldDisableCensusSync) {
            setValue('isCensusSyncEnabled', false, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            })
          }
        })()
      },
    })
  }

  const isCensusSyncEnabled = watch('isCensusSyncEnabled')
  const isHRISNotificationDisabled = watch('isHRISNotificationDisabled')
  const automaticStopCoverage = watch('automaticStopCoverage')

  return (
    <form>
      <Stack space={20}>
        <Stack direction="row" align="center" justify="space-between">
          <Stack direction="row" align="center" space={10}>
            <Stack direction="row" align="center" space={10}>
              <RHFToggle
                name="isCensusSyncEnabled"
                control={control}
                disabled={isUpdatingHrisIntegration}
                loading={isUpdatingHrisIntegration}
                onChange={handleCensusSyncEnabledChange}
              />
              <Typography size={16} color="#003146">
                API Enabled{' '}
                <Typography
                  color={
                    isCensusSyncEnabled ? TextColors.primary : TextColors.error
                  }
                  size={16}
                  bold
                >
                  {isCensusSyncEnabled ? 'ON' : 'OFF'}
                </Typography>
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" align="center" space={10}>
            <Switch
              disabled={
                isCensusApiVersionDisabled || isUpdatingCensusApiVersion
              }
              loading={isUpdatingCensusApiVersion}
              checkedChildren="CMv2"
              unCheckedChildren="CMv1"
              value={censusApiVersion === 'v2'}
              onChange={handleCensusApiVersionChange}
            />
            <Typography size={16} color="#003146">
              API Synced with{' '}
              <Typography color={TextColors.primary} size={16} bold>
                {censusApiVersion === 'v2' ? 'CMv2' : 'CMv1'}
              </Typography>
            </Typography>
          </Stack>
          <Stack direction="row" align="center" space={10}>
            <RHFToggle name="isHRISNotificationDisabled" control={control} />
            <Typography size={16} color="#003146">
              Notification Disabled{' '}
              <Typography
                color={
                  isHRISNotificationDisabled
                    ? TextColors.primary
                    : TextColors.error
                }
                size={16}
                bold
              >
                {isHRISNotificationDisabled ? 'ON' : 'OFF'}
              </Typography>
            </Typography>
          </Stack>
          <Stack direction="row" align="center" space={10}>
            <RHFToggle name="automaticStopCoverage" control={control} />
            <Typography size={16} color="#003146">
              Automatic Stop Coverage{' '}
              <Typography
                color={
                  automaticStopCoverage ? TextColors.primary : TextColors.error
                }
                size={16}
                bold
              >
                {automaticStopCoverage ? 'ON' : 'OFF'}
              </Typography>
            </Typography>
          </Stack>
        </Stack>
        <Stack space={12}>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <RHFInput
              name="webHookUrl"
              control={control}
              label="Web Hook URL"
              style={{ width: '100%' }}
            />
          </div>
          <Stack direction="row" justify="flex-start">
            <Button
              type="primary"
              onClick={handleSaveClick}
              loading={isUpdatingHrisIntegration}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </form>
  )
}
